/**
 * @file src/pages/invoice-creation-wizard/components/PreviewModal.jsx
 *
 * What changed from the original:
 *  - Local Button component removed — was shadowing the real Button from
 *    components/ui/Button and only supported 4 icons with no loading state
 *  - Seller section now uses formData.seller (name, vatNumber, registrationNumber)
 *    instead of hardcoded "Audit AI", "300000000000003", "1010010000"
 *  - Payment mode now shows formData.invoice.paymentMode instead of
 *    hardcoded "Cash" / "نقداً"
 *  - Escape key closes modal (accessibility)
 *  - Backdrop click closes modal
 */

import React, { useEffect } from "react";
import { motion } from "framer-motion";
import Button from "components/ui/Button";

// ---------------------------------------------------------------------------
// PreviewModal
// ---------------------------------------------------------------------------

const PreviewModal = ({ formData, onClose, onSubmit, isSubmitting }) => {
  // ── Close on Escape ───────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      aria-modal="true"
      role="dialog"
      aria-label="Invoice Preview"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-100 dark:border-gray-700 max-w-4xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-600">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Invoice Preview | معاينة الفاتورة
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Review your invoice before issuing. ZATCA reporting happens
                automatically.
              </p>
            </div>
            <Button variant="ghost" size="sm" iconName="X" onClick={onClose} />
          </div>
        </div>

        {/* ── Content ─────────────────────────────────────────────────────── */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-600 p-8">
            {/* Invoice Header — bilingual */}
            <div className="flex justify-between items-start mb-8">
              <div>
                <h2 className="text-2xl font-bold text-blue-900 dark:text-blue-400 mb-1">
                  Tax Invoice
                </h2>
                <h2
                  className="text-xl font-bold text-blue-900 dark:text-blue-400 mb-4"
                  dir="rtl"
                >
                  فاتورة ضريبية
                </h2>
              </div>
              <div className="text-right">
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formData.seller.registrationName || "—"}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  Saudi Arabia
                </div>
              </div>
            </div>

            {/* Invoice Details */}
            <div className="grid grid-cols-2 gap-6 mb-8 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              {[
                {
                  en: "Invoice reference number (IRN)",
                  ar: "الرقم التسلسلي",
                  val: formData.invoice.number,
                },
                {
                  en: "Issue Date",
                  ar: "تاريخ اصدار الفاتورة",
                  val: new Date(formData.invoice.date).toLocaleDateString(
                    "en-GB",
                  ),
                },
                {
                  en: "Supply Date",
                  ar: "تاريخ التوريد",
                  val: new Date(formData.invoice.date).toLocaleDateString(
                    "en-GB",
                  ),
                },
              ].map(({ en, ar, val }) => (
                <React.Fragment key={en}>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {en}
                    </p>
                    <p className="text-sm font-semibold text-blue-900 dark:text-blue-400">
                      {val}
                    </p>
                  </div>
                  <div className="text-right" dir="rtl">
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {ar}
                    </p>
                    <p className="text-sm font-semibold text-blue-900 dark:text-blue-400">
                      {val}
                    </p>
                  </div>
                </React.Fragment>
              ))}
            </div>

            {/* Seller Information */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-base font-semibold text-blue-900 dark:text-blue-400">
                  Seller Identification
                </h4>
                <h4
                  className="text-base font-semibold text-blue-900 dark:text-blue-400"
                  dir="rtl"
                >
                  هوية المورد
                </h4>
              </div>
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Seller Name
                    </p>
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-400">
                      {formData.seller.registrationName || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      VAT Registration Number
                    </p>
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-400">
                      {formData.seller.vatNumber || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Registration Number
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      {formData.seller.registrationNumber?.schemeId || "CRN"}
                    </p>
                    <p className="text-xs font-medium text-blue-900 dark:text-blue-400 mt-1">
                      {formData.seller.registrationNumber?.id || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Address
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      {[
                        formData.seller.address?.street,
                        formData.seller.address?.city,
                      ]
                        .filter(Boolean)
                        .join(", ") || "—"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Buyer Information */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-base font-semibold text-blue-900 dark:text-blue-400">
                  Buyer Identification
                </h4>
                <h4
                  className="text-base font-semibold text-blue-900 dark:text-blue-400"
                  dir="rtl"
                >
                  تحديد هوية العميل
                </h4>
              </div>
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Buyer Name
                    </p>
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-400">
                      {formData.customer.name || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      VAT Registration Number
                    </p>
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-400">
                      {formData.customer.vatNumber || "N/A"}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Buyer Address
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    {[
                      formData.customer.address.street,
                      formData.customer.address.district,
                      formData.customer.address.city,
                    ]
                      .filter(Boolean)
                      .join(", ") || "—"}
                  </p>
                </div>
              </div>
            </div>

            {/* Line Items Table */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-base font-semibold text-blue-900 dark:text-blue-400">
                  Line Items
                </h4>
                <h4
                  className="text-base font-semibold text-blue-900 dark:text-blue-400"
                  dir="rtl"
                >
                  معلومات وبيانات السلعة أو الخدمة
                </h4>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-200 dark:border-gray-600">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700">
                      {[
                        {
                          en: "Product/Service",
                          ar: "المنتج/الخدمة",
                          align: "text-left",
                        },
                        {
                          en: "Qty",
                          ar: "الكمية",
                          align: "text-center",
                          w: "w-16",
                        },
                        {
                          en: "Unit Price",
                          ar: "سعر الوحدة",
                          align: "text-right",
                          w: "w-24",
                        },
                        {
                          en: "Discount",
                          ar: "خصم",
                          align: "text-right",
                          w: "w-20",
                        },
                        {
                          en: "VAT%",
                          ar: "ضريبة%",
                          align: "text-center",
                          w: "w-16",
                        },
                        {
                          en: "VAT Amt",
                          ar: "مبلغ ضريبة",
                          align: "text-right",
                          w: "w-24",
                        },
                        {
                          en: "Excl VAT",
                          ar: "غير شامل",
                          align: "text-right",
                          w: "w-28",
                        },
                        {
                          en: "Incl VAT",
                          ar: "شامل",
                          align: "text-right",
                          w: "w-28",
                        },
                      ].map(({ en, ar, align, w = "" }) => (
                        <th
                          key={en}
                          className={`border border-gray-200 dark:border-gray-600 p-2 text-xs ${w}`}
                        >
                          <div
                            className={`${align} text-gray-700 dark:text-gray-300`}
                          >
                            {en}
                          </div>
                          <div
                            className="text-right text-gray-600 dark:text-gray-400 text-[10px]"
                            dir="rtl"
                          >
                            {ar}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {formData.lineItems.map((item, index) => {
                      const discount =
                        parseFloat(
                          item.allowanceChargeIndicator === "false"
                            ? item.allowanceChargeAmount
                            : 0,
                        ) || 0;
                      const subtotalExcl =
                        item.adjustedAmount ?? item.quantity * item.unitPrice;
                      const taxAmount = item.taxAmount ?? 0;
                      const subtotalIncl =
                        item.total ?? subtotalExcl + taxAmount;

                      return (
                        <tr
                          key={item.id}
                          className={
                            index % 2 === 0
                              ? "bg-white dark:bg-gray-800"
                              : "bg-gray-50 dark:bg-gray-750"
                          }
                        >
                          <td className="border border-gray-200 dark:border-gray-600 p-2 text-xs text-gray-700 dark:text-gray-300">
                            {item.description}
                          </td>
                          <td className="border border-gray-200 dark:border-gray-600 p-2 text-xs text-center text-gray-700 dark:text-gray-300">
                            {item.quantity}
                          </td>
                          <td className="border border-gray-200 dark:border-gray-600 p-2 text-xs text-right text-gray-700 dark:text-gray-300">
                            {item.unitPrice.toFixed(2)} SAR
                          </td>
                          <td className="border border-gray-200 dark:border-gray-600 p-2 text-xs text-right text-gray-700 dark:text-gray-300">
                            {discount.toFixed(2)}
                          </td>
                          <td className="border border-gray-200 dark:border-gray-600 p-2 text-xs text-center text-gray-700 dark:text-gray-300">
                            {item.taxRate}%
                          </td>
                          <td className="border border-gray-200 dark:border-gray-600 p-2 text-xs text-right text-gray-700 dark:text-gray-300">
                            {taxAmount.toFixed(2)} SAR
                          </td>
                          <td className="border border-gray-200 dark:border-gray-600 p-2 text-xs text-right text-gray-700 dark:text-gray-300">
                            {subtotalExcl.toFixed(2)} SAR
                          </td>
                          <td className="border border-gray-200 dark:border-gray-600 p-2 text-xs text-right text-blue-900 dark:text-blue-400 font-medium">
                            {subtotalIncl.toFixed(2)} SAR
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tax Summary */}
            <div className="bg-blue-50 dark:bg-gray-800 rounded-lg p-6 mb-8 border border-gray-200 dark:border-gray-600">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Invoice Taxable Amount (Exclusive of VAT)
                    </p>
                    <p className="text-base font-semibold text-blue-900 dark:text-blue-400">
                      {formData.totals.subtotal.toFixed(2)}{" "}
                      {formData.invoice.currency}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      VAT Total Amount
                    </p>
                    <p className="text-base font-semibold text-blue-900 dark:text-blue-400">
                      {formData.totals.totalTax.toFixed(2)}{" "}
                      {formData.invoice.currency}
                    </p>
                  </div>
                  <div className="pt-2 border-t border-gray-300 dark:border-gray-600">
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Invoice Gross Total (Inclusive of VAT)
                    </p>
                    <p className="text-lg font-bold text-blue-900 dark:text-blue-400">
                      {formData.totals.total.toFixed(2)}{" "}
                      {formData.invoice.currency}
                    </p>
                  </div>
                </div>
                <div className="space-y-4 text-right" dir="rtl">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      المبلغ الخاضع للضريبة (غير شامل ضريبة القيمة المضافة)
                    </p>
                    <p className="text-base font-semibold text-blue-900 dark:text-blue-400">
                      {formData.totals.subtotal.toFixed(2)}{" "}
                      {formData.invoice.currency}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      اجمالى مبلغ ضريبة القيمة المضافة
                    </p>
                    <p className="text-base font-semibold text-blue-900 dark:text-blue-400">
                      {formData.totals.totalTax.toFixed(2)}{" "}
                      {formData.invoice.currency}
                    </p>
                  </div>
                  <div className="pt-2 border-t border-gray-300 dark:border-gray-600">
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      إجمالي قيمة الفاتورة (شامل ضريبة القيمة المضافة)
                    </p>
                    <p className="text-lg font-bold text-blue-900 dark:text-blue-400">
                      {formData.totals.total.toFixed(2)}{" "}
                      {formData.invoice.currency}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Terms */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-base font-semibold text-blue-900 dark:text-blue-400">
                  Payment Terms
                </h4>
                <h4
                  className="text-base font-semibold text-blue-900 dark:text-blue-400"
                  dir="rtl"
                >
                  شروط الدفع
                </h4>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Payment means
                  </p>
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-400">
                    {formData.invoice.paymentMode || "Not specified"}
                  </p>
                </div>
                {formData.invoice.paymentNote && (
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Payment Note
                    </p>
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-400">
                      {formData.invoice.paymentNote}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            {formData.invoice.notes && (
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-base font-semibold text-blue-900 dark:text-blue-400">
                    Notes
                  </h4>
                  <h4
                    className="text-base font-semibold text-blue-900 dark:text-blue-400"
                    dir="rtl"
                  >
                    ملاحظات
                  </h4>
                </div>
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {formData.invoice.notes}
                  </p>
                </div>
              </div>
            )}

            {/* ZATCA Compliance Footer */}
            <div className="mt-8 pt-4 border-t border-gray-200 dark:border-gray-600 text-center text-xs text-gray-500 dark:text-gray-400 space-y-1">
              <p>This invoice is ZATCA Phase 1 &amp; Phase 2 compliant</p>
              <p>Generated by Audit AI - Enterprise E-invoicing Solution</p>
              <p className="text-[10px] mt-2">
                This is a ZATCA compliant electronic invoice generated in
                accordance with KSA e-invoicing regulations.
              </p>
            </div>
          </div>
        </div>

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-600">
          <div className="flex justify-between">
            <div className="flex gap-3">
              <Button variant="outline" size="md" iconName="Download">
                Download PDF
              </Button>
              <Button variant="outline" size="md" iconName="Copy">
                Copy Link
              </Button>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" size="md" onClick={onClose}>
                Back to Edit
              </Button>
              <Button
                variant="primary"
                size="md"
                iconName="Send"
                onClick={onSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                {isSubmitting ? "Issuing..." : "Issue Invoice"}
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PreviewModal;
